import React, { useEffect, useState, useRef, useCallback } from 'react'; // Added useCallback
import { Container, Form, Button, Alert, Image, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage, auth } from '../../../firebase';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc, query, where, getDocs as getFirestoreDocs } from 'firebase/firestore'; // Removed writeBatch
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import Layout from '../../../components/ui/Layout';
import BreadcrumbNavigation from '../../../components/ui/BreadcrumbNavigation';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('CreateProduct');

function newProductTypeId() {
  return window.crypto?.randomUUID?.() || `t_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

const emptyProductTypeRow = () => ({ id: newProductTypeId(), name: '', image: null, file: null });

const CreateProduct = () => {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { id: productId } = useParams();
  const isEditMode = !!productId;

  const [displayName, setDisplayName] = useState('');
  const [seoName, setSeoName] = useState('');
  const [description, setDescription] = useState('');
  const [attributes, setAttributes] = useState([{ name: '', value: '' }]);
  const [productTypes, setProductTypes] = useState([emptyProductTypeRow()]);
  const [status, setStatus] = useState('draft');
  const [tagsString, setTagsString] = useState('');
  const [categoryInput, setCategoryInput] = useState(''); // For the category text input
  const [categories, setCategories] = useState([]); // For storing existing categories
  const [files, setFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false); 
  const [pageLoading, setPageLoading] = useState(true); // Start true to load categories & product data

  const [isOtherCategory, setIsOtherCategory] = useState(false); // Add this state to track if "Other" is selected

  const fileInputRef = useRef();

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/admin", label: "Admin Panel" },
    { path: "/admin/products", label: "Products" },
    { label: isEditMode ? "Edit Product" : "Create Product" }
  ];

  // Fetch existing categories
  const fetchCategories = useCallback(async () => {
    try {
      const categoriesSnapshot = await getFirestoreDocs(collection(db, 'Categories'));
      const categoriesList = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(categoriesList);

    } catch (err) {
      logger.error("[CreateProduct] Failed to fetch categories:", err);
      setError(prev => `${prev  } Failed to load categories.`);
    }
  }, []);

  useEffect(() => {
    setPageLoading(true);
    fetchCategories(); // Fetch categories on mount/mode change

    if (isEditMode && productId) {
      const fetchProduct = async () => {
        try {
          const docRef = doc(db, 'Products', productId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDisplayName(data.display_name || '');
            setSeoName(data.seo_name || '');
            setDescription(data.description || '');
            setAttributes(data.attributes && data.attributes.length > 0 ? data.attributes : [{ name: '', value: '' }]);
            const pt = data.product_types;
            if (Array.isArray(pt) && pt.length > 0) {
              setProductTypes(pt.map((t) => ({
                id: t.id || newProductTypeId(),
                name: t.name || '',
                image: t.image || null,
                file: null,
              })));
            } else {
              setProductTypes([emptyProductTypeRow()]);
            }
            setStatus(data.status || 'draft');
            setExistingMedia(data.media || []);
            setTagsString((data.tags && Array.isArray(data.tags)) ? data.tags.join(', ') : '');
            setCategoryInput(data.category || ''); // Populate category input
          } else {
            setError('Product not found.');
          }
        } catch (err) {
          setError(`Failed to load product data. ${  err.message}`);
        } finally {
            // setPageLoading(false) will be called after categories are also fetched if needed
        }
      };
      fetchProduct().finally(() => setPageLoading(false));
    } else {
      // Create mode
      setDisplayName(''); setSeoName(''); setDescription(''); setAttributes([{ name: '', value: '' }]);
      setProductTypes([emptyProductTypeRow()]);
      setStatus('draft'); setFiles([]); setExistingMedia([]); setTagsString(''); setCategoryInput('');
      setPageLoading(false); // Categories are still fetched, then loading is false
    }
  }, [productId, isEditMode, fetchCategories]);

  const handleAttributeChange = (index, field, value) => {
    const newAttributes = [...attributes];
    newAttributes[index][field] = value;
    setAttributes(newAttributes);
  };
  const addAttribute = () => setAttributes([...attributes, { name: '', value: '' }]);
  const removeAttribute = (index) => setAttributes(attributes.filter((_, i) => i !== index));

  const handleProductTypeNameChange = (index, value) => {
    const next = [...productTypes];
    next[index] = { ...next[index], name: value };
    setProductTypes(next);
  };
  const handleProductTypeFileChange = (index, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      showNotification({ type: 'warning', title: 'File Too Large', message: `File ${f.name} is too large. Maximum size is 5MB.` });
      return;
    }
    if (!f.type.startsWith('image/')) {
      showNotification({ type: 'warning', title: 'Invalid File Type', message: 'Please select an image file for the product type.' });
      return;
    }
    const next = [...productTypes];
    next[index] = { ...next[index], file: f };
    setProductTypes(next);
  };
  const addProductType = () => setProductTypes([...productTypes, emptyProductTypeRow()]);
  const removeProductTypeRow = (index) => {
    if (productTypes.length <= 1) {
      setProductTypes([emptyProductTypeRow()]);
      return;
    }
    setProductTypes(productTypes.filter((_, i) => i !== index));
  };
  const clearProductTypeImage = (index) => {
    const next = [...productTypes];
    next[index] = { ...next[index], image: null, file: null };
    setProductTypes(next);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate files before setting state
    const validFiles = selectedFiles.filter(file => {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        showNotification({ type: 'warning', title: 'File Too Large', message: `File ${file.name} is too large. Maximum size is 5MB.` });
        return false;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification({ type: 'warning', title: 'Invalid File Type', message: `File ${file.name} is not an image. Please select image files only.` });
        return false;
      }
      
      return true;
    });
    
    setFiles(validFiles);
  };
  const handleRemoveExistingImage = (index) => setExistingMedia(prev => prev.filter((_, i) => i !== index));
  const handleRemoveNewImage = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const generateSeoName = (name) => {
    if (!name || !name.trim()) return '';
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  useEffect(() => {
    if (displayName && !seoName) {
      setSeoName(generateSeoName(displayName));
    }
  }, [displayName]); // Only auto-fill when displayName changes and seoName is empty

  const generateKeywords = (name, desc, category, tagsArray) => {
    const nameWords = name ? name.toLowerCase().split(/\s+/).filter(Boolean) : [];
    const descWords = desc ? desc.toLowerCase().split(/\s+/).filter(Boolean).slice(0, 10) : [];
    const categoryWords = category ? category.toLowerCase().split(/\s+/).filter(Boolean) : [];
    const tagKeywords = tagsArray ? tagsArray.map(tag => tag.toLowerCase().trim()).filter(Boolean) : [];
    const combinedName = name ? name.toLowerCase().replace(/\s+/g, '') : '';
    const keywords = new Set([
      ...nameWords, ...descWords, ...categoryWords, ...tagKeywords,
      ...(combinedName ? [combinedName] : [])
    ]);
    return Array.from(keywords);
  };

  const uploadFiles = async (storageKey) => {
    const uploadedMediaObjects = [];
    const uniqueIdPart = storageKey;
    
    for (const file of files) {
      try {
        
        // Better filename sanitization
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const timestamp = Date.now();
        const gcsPath = `images/products/${uniqueIdPart}/${timestamp}_${sanitizedName}`;
        
        // Ensure we have authentication before upload
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        
        // Refresh the user's token to ensure it's valid
        try {
          await currentUser.getIdToken(true);
 
        } catch (tokenError) {
          logger.error('Token refresh failed:', tokenError);
          throw new Error('Authentication token refresh failed');
        }
        
                                         const fileRef = ref(storage, gcsPath);
             const uploadTask = uploadBytesResumable(fileRef, file);
        
        await new Promise((resolve, _reject) => {
          uploadTask.on('state_changed',
                         (_snapshot) => { 
               // Progress tracking - removed unused variable
             }, 
            (error) => { 
              logger.error('Upload error for file:', file.name, error); 
              logger.error('Error details:', {
                code: error.code,
                message: error.message,
                serverResponse: error.serverResponse
              });
              
              // Fallback: use local public fallback image
              uploadedMediaObjects.push({
                name: 'fallback-product.svg',
                url: '/fallback-product.svg',
                path: 'public/fallback-product.svg',
                type: 'image/svg+xml',
                size: 0,
                created_at: Timestamp.fromDate(new Date())
              });
              resolve(); // Do not reject, continue with fallback
            },
            async () => {
              try {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  uploadedMediaObjects.push({ 
                    name: file.name, 
                    url: downloadURL, 
                    path: gcsPath, 
                    type: file.type, 
                    size: file.size, 
                    created_at: Timestamp.fromDate(new Date()) 
                });
                
                resolve();
              } catch (urlError) {
                logger.error('Error getting download URL for file:', file.name, urlError);
                              // Fallback: use local public fallback image
              uploadedMediaObjects.push({
                name: 'fallback-product.svg',
                url: '/fallback-product.svg',
                path: 'public/fallback-product.svg',
                type: 'image/svg+xml',
                size: 0,
                created_at: Timestamp.fromDate(new Date())
              });
                resolve();
              }
            }
          );
        });
      } catch (error) {
        logger.error('Unexpected error during upload:', error);
        // Fallback for unexpected errors
        uploadedMediaObjects.push({
          name: 'fallback-product.svg',
          url: '/fallback-product.svg',
          path: 'public/fallback-product.svg',
          type: 'image/svg+xml',
          size: 0,
          created_at: Timestamp.fromDate(new Date())
        });
      }
    }
    return uploadedMediaObjects;
  };

  const uploadProductTypesMedia = async (storageKey) => {
    const saved = [];
    for (const row of productTypes) {
      const name = row.name?.trim();
      if (!name) continue;
      let imageMedia = row.image;
      if (row.file) {
        const sanitizedName = row.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const gcsPath = `images/products/${storageKey}/types/${row.id}/${Date.now()}_${sanitizedName}`;
        const u = auth.currentUser;
        if (!u) throw new Error('User not authenticated');
        await u.getIdToken(true);
        const fileRef = ref(storage, gcsPath);
        const uploadTask = uploadBytesResumable(fileRef, row.file);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            () => {},
            (error) => {
              logger.error('Upload error for type image:', error);
              reject(new Error(error.message || 'Type image upload failed'));
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                imageMedia = {
                  name: row.file.name,
                  url: downloadURL,
                  path: gcsPath,
                  type: row.file.type,
                  size: row.file.size,
                  created_at: Timestamp.fromDate(new Date()),
                };
                resolve();
              } catch (urlError) {
                reject(urlError);
              }
            }
          );
        });
      }
      const hasPath = imageMedia && (imageMedia.path || imageMedia.url);
      if (!hasPath) {
        throw new Error(`Product type "${name}" requires an image.`);
      }
      saved.push({ id: row.id, name, image: imageMedia });
    }
    return saved;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!displayName || !categoryInput) { setError('Product name and category are required.'); return; }
    
    // Check authentication before upload
    if (!currentUser) {
      setError('You must be logged in to upload files.');
      return;
    }
    
    setLoading(true); setError(''); setSuccess('');
    try {
      const storageKey = productId || Date.now();
      const newUploadedMedia = await uploadFiles(storageKey);
      const finalProductTypes = await uploadProductTypesMedia(storageKey);
      const finalMedia = [...existingMedia, ...newUploadedMedia];
      const tagsArray = tagsString.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag !== '');
      const finalCategory = categoryInput.trim().toLowerCase();
      const keywords = generateKeywords(displayName, description, finalCategory, tagsArray);

      // Add new category to Categories collection if it doesn't exist
      if (finalCategory && !categories.some(cat => cat.name.toLowerCase() === finalCategory)) {
        const categoryQuery = query(collection(db, 'Categories'), where("name_lowercase", "==", finalCategory));
        const existingCategorySnap = await getFirestoreDocs(categoryQuery);
        if (existingCategorySnap.empty) {
            await addDoc(collection(db, 'Categories'), {
                name: categoryInput.trim(), // Store with original casing for display
                name_lowercase: finalCategory,
                createdAt: Timestamp.fromDate(new Date())
            });
            
            fetchCategories(); // Re-fetch categories to update datalist
        }
      }

      const finalSeoName = (seoName || generateSeoName(displayName) || '').trim();
      let uniqueSeoName = finalSeoName;
      if (uniqueSeoName) {
        const seoQuery = query(
          collection(db, 'Products'),
          where('seo_name', '==', uniqueSeoName)
        );
        const existingSeo = await getFirestoreDocs(seoQuery);
        const excludeId = isEditMode ? productId : null;
        const conflicts = existingSeo.docs.filter(d => d.id !== excludeId);
        if (conflicts.length > 0) {
          let suffix = 2;
          while (true) {
            const candidate = `${uniqueSeoName}-${suffix}`;
            const candQuery = query(collection(db, 'Products'), where('seo_name', '==', candidate));
            const candSnap = await getFirestoreDocs(candQuery);
            const candConflicts = candSnap.docs.filter(d => d.id !== excludeId);
            if (candConflicts.length === 0) {
              uniqueSeoName = candidate;
              break;
            }
            suffix++;
          }
        }
      }

      const productData = {
        display_name: displayName, description, category: categoryInput.trim(), // Save original casing category
        seo_name: uniqueSeoName || null,
        attributes: attributes.filter(attr => attr.name && attr.value),
        product_types: finalProductTypes,
        status, media: finalMedia, tags: tagsArray, search_keywords: keywords,
        updated_at: Timestamp.fromDate(new Date()),
      };

      if (isEditMode) {
        const docRef = doc(db, 'Products', productId);
        await updateDoc(docRef, productData);
        setSuccess('Product updated successfully!');
      } else {
        productData.created_at = Timestamp.fromDate(new Date());
        productData.created_by = { uid: currentUser.uid, email: currentUser.email };
        
        await addDoc(collection(db, 'Products'), productData);
        setSuccess('Product created successfully!');
        setDisplayName(''); setSeoName(''); setDescription(''); setAttributes([{ name: '', value: '' }]); 
        setProductTypes([emptyProductTypeRow()]);
        setStatus('draft'); setFiles([]); setTagsString(''); setCategoryInput('');
        if(fileInputRef.current) fileInputRef.current.value = '';
      }
      setTimeout(() => navigate('/admin/products'), 1500);
    } catch (err) {
      setError(`Operation failed: ${  err.message}`); logger.error(err);
    }
    setLoading(false);
  };

  const handleGoBack = () => navigate(-1);

  // Update this handler to manage dropdown/text input switch
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "__other__") {
      setIsOtherCategory(true);
      setCategoryInput('');
    } else {
      setIsOtherCategory(false);
      setCategoryInput(value);
    }
  };

  if (pageLoading) { // Simplified page loading check
    return <Layout><Container className="text-center mt-5"><Spinner animation="border" /> <p>Loading form data...</p></Container></Layout>;
  }

  return (
    <Layout>
      <Container className="mt-3 px-3">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={handleGoBack} className="mb-2">&larr; Go Back</Button>
            </Col>
            <Col><BreadcrumbNavigation items={breadcrumbItems} /></Col>
        </Row>
        <h1>{isEditMode ? 'Edit Product' : 'Create Product'}</h1>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="productDisplayName">
            <Form.Label className="fw-semibold">Product Name</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter product name" 
              value={displayName} 
              onChange={(e) => setDisplayName(e.target.value)} 
              required 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="productSeoName">
            <Form.Label className="fw-semibold">SEO-friendly URL name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., organic-milk (auto-generated from name)"
              value={seoName}
              onChange={(e) => setSeoName(e.target.value)}
              className="w-100"
              style={{
                fontSize: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
            <Form.Text className="text-muted small">
              URL-friendly name for SEO. Use lowercase letters, numbers, and hyphens only. Auto-generated from product name when empty.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="productCategory">
            <Form.Label className="fw-semibold">Category</Form.Label>
            <Form.Select
              value={isOtherCategory ? "__other__" : categoryInput}
              onChange={handleCategoryChange}
              required
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
              <option value="__other__">Other (type new...)</option>
            </Form.Select>
            {isOtherCategory && (
              <Form.Control
                className="mt-2 w-100"
                type="text"
                placeholder="Type new category"
                value={categoryInput}
                onChange={e => setCategoryInput(e.target.value)}
                required
                style={{ 
                  fontSize: '16px', // Prevents zoom on iOS
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #dee2e6',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              />
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="productDescription">
            <Form.Label className="fw-semibold">Description</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              placeholder="Product description" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="productTags">
            <Form.Label className="fw-semibold">Tags (comma-separated)</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="e.g., organic, best-seller" 
              value={tagsString} 
              onChange={(e) => setTagsString(e.target.value)} 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          {attributes.map((attr, index) => (
            <Row key={index} className="mb-2 align-items-end">
              <Col xs={12} md={5} className="mb-2 mb-md-0">
                <Form.Group controlId={`attrName-${index}`}>
                  {index === 0 && <Form.Label className="fw-semibold">Attribute Name</Form.Label>}
                  <Form.Control 
                    type="text" 
                    placeholder="e.g., Color" 
                    value={attr.name} 
                    onChange={(e) => handleAttributeChange(index, 'name', e.target.value)} 
                    className="w-100"
                    style={{ 
                      fontSize: '16px', // Prevents zoom on iOS
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #dee2e6',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={5} className="mb-2 mb-md-0">
                <Form.Group controlId={`attrValue-${index}`}>
                  {index === 0 && <Form.Label className="fw-semibold">Attribute Value</Form.Label>}
                  <Form.Control 
                    type="text" 
                    placeholder="e.g., Red" 
                    value={attr.value} 
                    onChange={(e) => handleAttributeChange(index, 'value', e.target.value)} 
                    className="w-100"
                    style={{ 
                      fontSize: '16px', // Prevents zoom on iOS
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #dee2e6',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2} className="d-flex align-items-center">
                {attributes.length > 1 && (
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={() => removeAttribute(index)} 
                    className="w-100 w-md-auto"
                    style={{ 
                      minHeight: '44px', // Better touch target
                      fontSize: '14px',
                      padding: '8px 16px'
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Col>
            </Row>))}
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={addAttribute} 
            className="mb-3 w-100 w-md-auto"
            style={{ 
              minHeight: '44px', // Better touch target
              fontSize: '14px',
              padding: '8px 16px'
            }}
          >
            + Add Attribute
          </Button>

          <hr className="my-4" />
          <p className="fw-semibold mb-2">Product types (optional)</p>
          <p className="text-muted small mb-3">Each type has a name and image. When a product has types, inventory uses a price per selected type (shown as checkboxes on add/edit inventory).</p>
          {productTypes.map((row, index) => (
            <Row key={row.id} className="mb-3 align-items-end">
              <Col xs={12} md={4} className="mb-2 mb-md-0">
                <Form.Group controlId={`ptype-name-${row.id}`}>
                  {index === 0 && <Form.Label className="fw-semibold">Type name</Form.Label>}
                  <Form.Control
                    type="text"
                    placeholder="e.g., Small / Large"
                    value={row.name}
                    onChange={(e) => handleProductTypeNameChange(index, e.target.value)}
                    className="w-100"
                    style={{ fontSize: '16px', padding: '12px 16px', borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4} className="mb-2 mb-md-0">
                <Form.Group controlId={`ptype-img-${row.id}`}>
                  {index === 0 && <Form.Label className="fw-semibold">Type image</Form.Label>}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleProductTypeFileChange(index, e)}
                    className="w-100"
                    style={{ fontSize: '16px', padding: '12px 16px', borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4} className="d-flex flex-wrap align-items-center gap-2">
                {(row.file || row.image?.url) && (
                  <>
                    <Image
                      thumbnail
                      style={{ maxWidth: 56, maxHeight: 56, objectFit: 'cover' }}
                      src={row.file ? URL.createObjectURL(row.file) : row.image.url}
                      alt=""
                    />
                    <Button type="button" variant="outline-danger" size="sm" onClick={() => clearProductTypeImage(index)}>
                      Clear image
                    </Button>
                  </>
                )}
                {productTypes.length > 1 && (
                  <Button type="button" variant="outline-secondary" size="sm" onClick={() => removeProductTypeRow(index)}>
                    Remove
                  </Button>
                )}
              </Col>
            </Row>
          ))}
          <Button type="button" variant="outline-secondary" size="sm" onClick={addProductType} className="mb-3 w-100 w-md-auto" style={{ minHeight: '44px' }}>
            + Add product type
          </Button>

          <Form.Group className="mb-3" controlId="productStatus">
            <Form.Label className="fw-semibold">Status</Form.Label>
            <Form.Select 
              value={status} 
              onChange={(e) => setStatus(e.target.value)}
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="productFormFiles" className="mb-3">
            <Form.Label className="fw-semibold">Upload Images</Form.Label>
            <Form.Control 
              type="file" 
              multiple 
              onChange={handleFileChange} 
              ref={fileInputRef} 
              accept="image/*" 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          {isEditMode && existingMedia.length > 0 && (<div className="mb-3"><p className="fw-bold">Current Images:</p><div className="d-flex flex-wrap">{existingMedia.map((media, index) => (<div key={`existing-${index}`} className="me-2 mb-2 border p-1 text-center"><Image src={media.url} alt={media.name || `Image ${index+1}`} thumbnail style={{maxWidth: '100px', maxHeight: '100px', objectFit: 'contain'}} /><Button variant="outline-danger" size="sm" className="mt-1 d-block mx-auto" onClick={() => handleRemoveExistingImage(index)}>Remove</Button></div>))}</div></div>)}
          {files.length > 0 && (<div className="mb-3"><p className="fw-bold">New Images to Upload:</p><div className="d-flex flex-wrap">{files.map((file, index) => (<div key={`new-${index}`} className="me-2 mb-2 border p-1 text-center"><Image src={URL.createObjectURL(file)} alt={file.name} thumbnail style={{maxWidth: '100px', maxHeight: '100px', objectFit: 'contain'}} /><Button variant="outline-danger" size="sm" className="mt-1 d-block mx-auto" onClick={() => handleRemoveNewImage(index)}>Remove</Button></div>))}</div></div>)}
          

          
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || pageLoading}
            className="w-100 w-md-auto"
            size="lg"
            style={{ 
              minHeight: '48px', // Better touch target
              fontSize: '16px',
              padding: '12px 24px'
            }}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Processing...
              </>
            ) : (
              isEditMode ? 'Update Product' : 'Create Product'
            )}
          </Button>
        </Form>
      </Container>
    </Layout>
  );
};

export default CreateProduct;
